import { quat, vec3 } from 'gl-matrix';

export interface TwoBoneIkResult {
  /** New world-space position for the mid joint */
  midPos: vec3;
  /** New world-space position for the tip joint (clamped to reach) */
  tipPos: vec3;
}

/**
 * Analytic two-bone IK solver.
 *
 * Given world-space positions of root (A), mid (B), and tip (C),
 * a target position (T), and a pole vector (P) that controls the
 * bend plane, computes the new mid and tip positions that place
 * the tip at (or as close as possible to) the target while preserving
 * bone lengths.
 *
 * Root position is never moved.
 */
export function solveTwoBoneIk(
  rootPos: vec3,
  midPos: vec3,
  tipPos: vec3,
  targetPos: vec3,
  polePos: vec3,
): TwoBoneIkResult {
  const lenAB = vec3.distance(rootPos, midPos);
  const lenBC = vec3.distance(midPos, tipPos);

  // Direction and distance from root to target, clamped to reachable range
  const toTarget = vec3.sub(vec3.create(), targetPos, rootPos);
  let lenAT = vec3.length(toTarget);
  const maxReach = lenAB + lenBC - 0.001;
  const minReach = Math.abs(lenAB - lenBC) + 0.001;
  lenAT = Math.max(minReach, Math.min(maxReach, lenAT));

  // Law of cosines: angle at root between (root→target) and (root→mid)
  const cosAngleRoot = (lenAB * lenAB + lenAT * lenAT - lenBC * lenBC) / (2 * lenAB * lenAT);
  const angleRoot = Math.acos(Math.max(-1, Math.min(1, cosAngleRoot)));

  // Build the bend plane from root→target and root→pole
  const targetDir = vec3.normalize(vec3.create(), toTarget);
  const toPole = vec3.sub(vec3.create(), polePos, rootPos);

  // Remove component of toPole along targetDir to get the perpendicular bend direction
  const poleAlongTarget = vec3.scale(vec3.create(), targetDir, vec3.dot(toPole, targetDir));
  const bendDir = vec3.sub(vec3.create(), toPole, poleAlongTarget);
  const bendLen = vec3.length(bendDir);

  // Fallback: if pole is collinear with target, use a world-up cross
  if (bendLen < 0.0001) {
    const up = vec3.fromValues(0, 1, 0);
    vec3.cross(bendDir, targetDir, up);
    if (vec3.length(bendDir) < 0.0001) {
      vec3.cross(bendDir, targetDir, vec3.fromValues(1, 0, 0));
    }
  }
  vec3.normalize(bendDir, bendDir);

  // Bend axis is perpendicular to both targetDir and bendDir
  const bendAxis = vec3.cross(vec3.create(), targetDir, bendDir);
  vec3.normalize(bendAxis, bendAxis);

  // Desired direction from root to mid: rotate targetDir by angleRoot around bendAxis
  const rootToMidDir = vec3.create();
  const rootRotQ = quat.setAxisAngle(quat.create(), bendAxis, angleRoot);
  vec3.transformQuat(rootToMidDir, targetDir, rootRotQ);

  // Compute new joint positions (bone lengths are preserved by construction)
  const newMidPos = vec3.scaleAndAdd(vec3.create(), rootPos, rootToMidDir, lenAB);

  // Tip goes to the clamped target position
  const clampedTarget = vec3.scaleAndAdd(vec3.create(), rootPos, targetDir, lenAT);

  return { midPos: newMidPos, tipPos: clampedTarget };
}
